import type { DragToRotateHandleProps } from "@/hooks/use-drag-to-rotate";
import { ROTATE_CURSOR } from "@/lib/rotate-cursor";

/**
 * Marker class on the rotate-handle knob. Editing surfaces add this to their
 * `react-zoom-pan-pinch` `panning.excluded` list so a rotate-drag opts out of
 * the plan pan. This is the real pan-isolation mechanism: the pan controller
 * binds its own native pointer listeners, so the handle merely stopping React's
 * synthetic event propagation does NOT stop a pan (the prior bug). Exported as a
 * constant so the handle's class and the exclusion list cannot drift apart.
 * See [[directional-pins]] and [[pin-rotate-handle.spec]].
 */
export const PIN_ROTATE_HANDLE_CLASS = "pin-rotate-handle";

export interface PinRotateHandleProps {
  /** Pin location in image-relative coords `[0,1]` — the handle pivots here. */
  xPct: number;
  yPct: number;
  /** Current bearing in degrees `[0, 360)`, or null when no direction is set. */
  bearing: number | null;
  /** Angle the handle rests at when `bearing` is null. Default 0 (drawing-top). */
  restBearing?: number;
  /**
   * On-screen radius (px at `--pin-scale: 1`) from the pin to the knob. Set to
   * the cone's tip radius so the knob lands exactly on the cone tip.
   */
  radiusPx: number;
  /** Gesture handlers from the shared rotate gesture (`useDragToRotate`). */
  handleProps: DragToRotateHandleProps;
  /** Accessible label — differs per surface (placement vs editing). */
  ariaLabel: string;
}

/**
 * The shared **arrow-lever** rotate handle: a stem from the pin to a knob at the
 * cone's tip, the knob carrying an outward arrow that points the way the pin
 * faces. Dragging the knob sets the bearing (gesture owned by [[directional-pins]]
 * via `useDragToRotate`, spread in as `handleProps`).
 *
 * The whole handle pivots around the pin (`rotate(bearing)`, `transform-origin`
 * at the pin) and rides `--pin-scale` so it holds a constant on-screen size and
 * pin distance at any zoom — matching the pin marker and the cone. The inner
 * arrow is drawn pointing straight up in local coordinates, so the wrapper's
 * `rotate(bearing)` turns it to point exactly along the bearing.
 *
 * Replaces the bare dot the first cut used, which was indistinguishable from the
 * pin marker. See [[pin-rotate-handle.spec]].
 */
export function PinRotateHandle({
  xPct,
  yPct,
  bearing,
  restBearing = 0,
  radiusPx,
  handleProps,
  ariaLabel,
}: PinRotateHandleProps) {
  const angle = bearing ?? restBearing;
  return (
    <div
      className="pointer-events-none absolute"
      style={{
        left: `${xPct * 100}%`,
        top: `${yPct * 100}%`,
        width: 0,
        height: 0,
        transform: `rotate(${angle}deg) scale(var(--pin-scale, 1))`,
        transformOrigin: "0 0",
      }}
    >
      {/* Stem — the lever arm from the pin (0,0) up to the knob at the cone tip.
          Decorative; the knob owns the pointer events. */}
      <div
        aria-hidden
        className="absolute rounded-full bg-tertiary"
        style={{
          left: 0,
          top: -radiusPx,
          height: radiusPx,
          width: 2,
          transform: "translateX(-50%)",
        }}
      />
      {/* Knob — the grab target. Carries PIN_ROTATE_HANDLE_CLASS so the surface
          excludes it from the plan pan. White fill + accent ring + accent arrow
          reads as a control, deliberately inverting the pin marker's filled dot
          so the two never look alike. */}
      <div
        {...handleProps}
        role="slider"
        aria-label={ariaLabel}
        aria-valuenow={bearing ?? undefined}
        aria-valuemin={0}
        aria-valuemax={360}
        className={`${PIN_ROTATE_HANDLE_CLASS} absolute flex h-11 w-11 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border-2 border-tertiary bg-white text-tertiary shadow-md`}
        style={{
          ...handleProps.style,
          left: 0,
          top: -radiusPx,
          pointerEvents: "auto",
          cursor: ROTATE_CURSOR,
        }}
      >
        <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor" aria-hidden>
          <path d="M12 3 L18 19 L12 15 L6 19 Z" />
        </svg>
      </div>
    </div>
  );
}
